import React from 'react';

import { PropertySelectField } from '@paragon/client-tools';
import { AbsoluteBearing, bearingAnchors } from '@paragon/contract-library';
import { DistanceField } from '@paragon/material';

import {
  changeBearingAnchorAbsoluteGeometry,
  setBearingWidthAbsoluteGeometry,
  shiftBearingPositionAbsoluteGeometry,
} from '../../../../store/actions/editBearingActions';
import { calculateActiveComponentMembersMinMax } from '../../../../store/selectors/boundsSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';

interface Props {
  readonly bearing: AbsoluteBearing;
}

export function BearingAbsoluteGeometrySubPanel({ bearing }: Props) {
  const dispatch = useAppDispatch();

  const activeComponentDesignGuid = useAppSelector((state) => state.selections.activeComponentDesignGuid);
  const componentBounds = useAppSelector(calculateActiveComponentMembersMinMax);
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);

  if (activeComponentDesignGuid == null) return null;

  const leftDistance = componentBounds != null ? bearing.centerPointX - bearing.width / 2 - componentBounds.minX : null;
  const rightDistance =
    componentBounds != null ? componentBounds.maxX - (bearing.centerPointX + bearing.width / 2) : null;

  return (
    <>
      <DistanceField
        label="Width"
        value={bearing.width}
        disabled={isViewOnly}
        onSubmit={(width) => {
          dispatch(setBearingWidthAbsoluteGeometry(bearing, activeComponentDesignGuid, width));
        }}
      />
      <DistanceField
        label="Left Distance"
        value={leftDistance}
        canBeNull
        canSubmitWhenNull={false}
        disabled={isViewOnly || leftDistance == null}
        nullPlaceholder="Error calculating component bounds"
        onSubmit={(newLeftDistance) => {
          if (leftDistance == null) throw new Error('Expected left distance to be set.');
          dispatch(
            shiftBearingPositionAbsoluteGeometry(bearing, activeComponentDesignGuid, newLeftDistance - leftDistance),
          );
        }}
      />
      <DistanceField
        label="Right Distance"
        value={rightDistance}
        canBeNull
        canSubmitWhenNull={false}
        disabled={isViewOnly || rightDistance == null}
        nullPlaceholder="Error calculating component bounds"
        onSubmit={(newRightDistance) => {
          if (rightDistance == null) throw new Error('Expected right distance to be set.');
          dispatch(
            shiftBearingPositionAbsoluteGeometry(bearing, activeComponentDesignGuid, rightDistance - newRightDistance),
          );
        }}
      />
      <PropertySelectField
        property="Anchor"
        value={bearing.anchor}
        options={bearingAnchors}
        disabled={isViewOnly}
        submit={(anchor) => {
          dispatch(changeBearingAnchorAbsoluteGeometry(bearing, activeComponentDesignGuid, anchor));
        }}
      />
    </>
  );
}
